/**
 * Browser client for server-backed custom template endpoints.
 */

import { authedFetch } from '../shared/authed-fetch.js';

const LOCALHOST_API_ORIGIN = 'http://localhost:3001';
const AUTH_SESSION_HINT_KEY = 'pb:auth-session:v1';
const AUTH_TOKEN_KEY = 'pb:auth-token:v1';
const AUTH_ACCOUNT_KEY = 'pb:auth-account:v1';

export class TemplateApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'TemplateApiError';
    this.status = status;
    this.data = data;
    // The server's typed `code` is the only thing that separates several
    // distinct failures sharing one status — a kill switch and a real fault
    // both answer 503. It is surfaced HERE, on the error, because that is
    // where every call site already looks: the guest review client sets
    // `err.code` itself, so `err?.code === 'comments_disabled'` reads true
    // there and was silently false for every member route, which left the
    // comment poller retrying a permanently disabled endpoint forever.
    this.code = (data && typeof data === 'object' && data.code) || null;
  }
}

export function getTemplateApiOrigin(win = globalThis.window) {
  const explicit =
    typeof win?.__PB_TEMPLATE_ENDPOINT === 'string' ? win.__PB_TEMPLATE_ENDPOINT.trim() : '';
  if (explicit) return new URL(explicit).origin;

  const preview =
    typeof win?.__PB_PREVIEW_ENDPOINT === 'string' ? win.__PB_PREVIEW_ENDPOINT.trim() : '';
  if (preview) return new URL(preview).origin;

  const compose =
    typeof win?.__PB_COMPOSE_ENDPOINT === 'string' ? win.__PB_COMPOSE_ENDPOINT.trim() : '';
  if (compose) return new URL(compose).origin;

  const protocol = win?.location?.protocol;
  const origin = win?.location?.origin;
  if ((protocol === 'http:' || protocol === 'https:') && origin) return origin;
  return LOCALHOST_API_ORIGIN;
}

function isLegacyAdminAllowed() {
  return Boolean(globalThis.window?.__PB_ALLOW_LEGACY_ADMIN);
}

export function hasAuthSessionHint() {
  if (isLegacyAdminAllowed()) return true;
  try {
    return globalThis.localStorage?.getItem(AUTH_SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

export function rememberAuthSessionHint() {
  try {
    globalThis.localStorage?.setItem(AUTH_SESSION_HINT_KEY, '1');
  } catch {
    // Storage may be unavailable in private mode; auth still works via cookie.
  }
}

export function getAuthBearerToken() {
  try {
    return globalThis.localStorage?.getItem(AUTH_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function rememberAuthSession(payload = {}) {
  rememberAuthSessionHint();
  try {
    const sub = typeof payload?.sub === 'string' ? payload.sub.trim() : '';
    if (sub && sub !== 'admin') {
      globalThis.localStorage?.setItem(
        AUTH_ACCOUNT_KEY,
        JSON.stringify({
          sub,
          email: typeof payload?.email === 'string' ? payload.email : '',
          authMode: typeof payload?.authMode === 'string' ? payload.authMode : ''
        })
      );
    }
  } catch {
    // Account-scoped browser settings fall back to unscoped storage.
  }
  const token = typeof payload?.token === 'string' ? payload.token.trim() : '';
  if (!token) return;
  try {
    globalThis.localStorage?.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // Cookies may still work when storage is unavailable.
  }
}

export function forgetAuthSessionHint() {
  try {
    globalThis.localStorage?.removeItem(AUTH_SESSION_HINT_KEY);
  } catch {
    // ignore
  }
}

export function forgetAuthSession() {
  forgetAuthSessionHint();
  try {
    globalThis.localStorage?.removeItem(AUTH_TOKEN_KEY);
    globalThis.localStorage?.removeItem(AUTH_ACCOUNT_KEY);
  } catch {
    // ignore
  }
}

export function getAuthHeaders(headers = {}) {
  const token = getAuthBearerToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers || {})
  };
}

async function parseJsonResponse(resp) {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    if (resp.status === 401) clearListTemplatesCache();
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return data;
}

async function parseBlobResponse(resp) {
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    if (resp.status === 401) clearListTemplatesCache();
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return await resp.blob();
}

/**
 * Upload a template bundle.
 *
 * @param {{meta: object, zipBlob: Blob, html?: string, signal?: AbortSignal}} args
 *   `signal` aborts the upload itself. Template bundles run to 20 MB, so a user
 *   who closes the modal on a slow connection was previously stuck waiting for
 *   a request whose result nothing would read.
 */
export async function saveTemplate({ meta, zipBlob, html, signal }) {
  const origin = getTemplateApiOrigin();
  const form = new FormData();
  form.append('meta', JSON.stringify(meta || {}));
  form.append('bundle', zipBlob, `${meta?.slug || 'template'}.pb.zip`);
  form.append('html', html || '');

  const resp = await authedFetch(`${origin}/api/templates/save`, {
    method: 'POST',
    body: form,
    signal
  });
  return parseJsonResponse(resp);
}

export async function getTemplateStatus(slug) {
  const origin = getTemplateApiOrigin();
  const resp = await authedFetch(`${origin}/api/templates/${encodeURIComponent(slug)}`, {
    method: 'GET'
  });
  return parseJsonResponse(resp);
}

const LIST_CACHE_KEY = 'pb:templates-list:v1';
const LIST_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h hard cap on stale-served data

function readListCache() {
  try {
    const raw = globalThis.localStorage?.getItem(LIST_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.fetchedAt !== 'number') return null;
    if (Date.now() - parsed.fetchedAt > LIST_CACHE_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeListCache(entry) {
  try {
    globalThis.localStorage?.setItem(LIST_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Quota, private mode, etc. — non-fatal.
  }
}

export function clearListTemplatesCache() {
  try {
    globalThis.localStorage?.removeItem(LIST_CACHE_KEY);
  } catch {
    // ignore
  }
  _inflightListPromise = null;
}

let _inflightListPromise = null;

async function fetchTemplatesFromServer(etag) {
  const origin = getTemplateApiOrigin();
  const headers = {};
  if (etag) headers['If-None-Match'] = etag;
  const resp = await authedFetch(`${origin}/api/templates/list`, {
    method: 'GET',
    headers
  });
  if (resp.status === 304) {
    return { notModified: true, etag, data: null };
  }
  const data = await parseJsonResponse(resp);
  return { notModified: false, etag: resp.headers.get('ETag') || null, data };
}

/**
 * SWR-style template list fetch.
 *
 * Returns `{ data, fresh }` synchronously where:
 *   - `data`  is the cached server response (or null on cold start).
 *   - `fresh` is a Promise that resolves to the freshly-fetched response
 *     (or null when the server returns 304 — no change).
 *
 * Callers can render `data` immediately and re-render when `fresh` resolves.
 * Pass `{ forceRevalidate: true }` to ignore the cache and refetch.
 */
export function listTemplates(options = {}) {
  const { forceRevalidate = false } = options;
  if (!hasAuthSessionHint()) {
    return { data: null, fresh: Promise.resolve(null) };
  }
  const cached = forceRevalidate ? null : readListCache();
  const data = cached?.data || null;

  const fresh = (async () => {
    // Coalesce parallel callers (e.g. hover prefetch + click).
    if (!_inflightListPromise) {
      _inflightListPromise = (async () => {
        try {
          const result = await fetchTemplatesFromServer(cached?.etag || null);
          if (result.notModified && cached) {
            // Refresh the timestamp so cache stays warm.
            writeListCache({ ...cached, fetchedAt: Date.now() });
            return null;
          }
          if (!result.notModified) {
            writeListCache({ data: result.data, etag: result.etag, fetchedAt: Date.now() });
            return result.data;
          }
          return result.data;
        } finally {
          _inflightListPromise = null;
        }
      })();
    }
    return _inflightListPromise;
  })();

  return { data, fresh };
}

/**
 * Fire-and-forget cache warmer. Safe to call on hover/focus.
 * Idempotent — multiple calls share one inflight fetch.
 */
export function prefetchTemplates() {
  const { fresh } = listTemplates();
  // Swallow rejection — prefetch is best-effort.
  fresh.catch(() => {});
}

export async function getTemplateZip(slug) {
  const origin = getTemplateApiOrigin();
  const resp = await authedFetch(`${origin}/api/templates/${encodeURIComponent(slug)}/zip`, {
    method: 'GET'
  });
  return parseBlobResponse(resp);
}

export function getTemplateThumbnailUrl(slug) {
  return `${getTemplateApiOrigin()}/api/templates/${encodeURIComponent(slug)}/thumbnail.png`;
}

export async function deleteTemplate(slug) {
  const origin = getTemplateApiOrigin();
  const resp = await authedFetch(`${origin}/api/templates/${encodeURIComponent(slug)}`, {
    method: 'DELETE'
  });
  return parseJsonResponse(resp);
}
